import type { SupabaseClient } from '@supabase/supabase-js'

type TradeRow = Record<string, unknown>

/**
 * Updates quantity/position_size after add/reduce. When a reduce brings quantity to 0,
 * closes the trade in a second update so DB close triggers use OLD.quantity = 0 and
 * do not double-count exit amounts already recorded via trade_history.
 */
export async function updateTradeQuantityAfterEvent(
  supabase: SupabaseClient,
  tradeId: string,
  params: {
    eventType: string
    updatedQuantity: number
    updatedPositionSize: number
    exitDateIso: string
    exitPrice: number
  },
): Promise<{ data: TradeRow | null; error: Error | null }> {
  const { eventType, updatedQuantity, updatedPositionSize, exitDateIso, exitPrice } = params
  const positionUpdate = {
    quantity: updatedQuantity,
    position_size: updatedPositionSize,
  }

  const fullyClosedByReduce = eventType === 'reduce_size' && updatedQuantity === 0

  if (!fullyClosedByReduce) {
    const { error } = await supabase.from('trades').update(positionUpdate).eq('id', tradeId)
    return { data: null, error: error ? new Error(error.message) : null }
  }

  const { error: qtyError } = await supabase.from('trades').update(positionUpdate).eq('id', tradeId)
  if (qtyError) return { data: null, error: new Error(qtyError.message) }

  const { data, error: closeError } = await supabase
    .from('trades')
    .update({
      exit_date: exitDateIso,
      exit_price: exitPrice,
      quantity: 0,
      position_size: 0,
    })
    .eq('id', tradeId)
    .select()
    .single()

  return { data: data as TradeRow | null, error: closeError ? new Error(closeError.message) : null }
}
